class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    this.startViewportTracking();
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.stopViewportTracking();
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  updateViewport() {
    const viewport = window.visualViewport;
    const top = viewport?.offsetTop || 0;
    const height = viewport?.height || window.innerHeight;
    const bottom = top + height;
    const previewBar = document.getElementById('PBarNextFrameWrapper') ||
      document.getElementById('preview-bar-iframe');
    const rect = previewBar?.getBoundingClientRect();
    const style = previewBar && window.getComputedStyle(previewBar);
    const obstruction = rect && style.display !== 'none' && style.visibility !== 'hidden' &&
      rect.width > 0 && rect.height > 0 && rect.bottom >= bottom - 2 && rect.top < bottom
      ? Math.min(height, bottom - Math.max(top, rect.top)) : 0;
    this.style.setProperty('--cart-viewport-top', `${top}px`);
    this.style.setProperty('--cart-viewport-height', `${Math.max(1, height - obstruction)}px`);
    if (this.previewBar !== previewBar) {
      this.previewResizeObserver?.disconnect();
      this.previewAttributeObserver?.disconnect();
      this.previewBar = previewBar;
      if (previewBar) {
        this.previewResizeObserver?.observe(previewBar);
        this.previewAttributeObserver?.observe(previewBar, { attributes: true });
      }
    }
  }

  startViewportTracking() {
    this.stopViewportTracking();
    this.viewportUpdate = this.updateViewport.bind(this);
    this.previewResizeObserver = new ResizeObserver(this.viewportUpdate);
    this.previewAttributeObserver = new MutationObserver(this.viewportUpdate);
    this.previewInsertionObserver = new MutationObserver(this.viewportUpdate);
    this.previewInsertionObserver.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', this.viewportUpdate);
    window.visualViewport?.addEventListener('resize', this.viewportUpdate);
    window.visualViewport?.addEventListener('scroll', this.viewportUpdate);
    this.updateViewport();
  }

  stopViewportTracking() {
    window.removeEventListener('resize', this.viewportUpdate);
    window.visualViewport?.removeEventListener('resize', this.viewportUpdate);
    window.visualViewport?.removeEventListener('scroll', this.viewportUpdate);
    this.previewResizeObserver?.disconnect();
    this.previewAttributeObserver?.disconnect();
    this.previewInsertionObserver?.disconnect();
    this.previewBar = null;
  }

  disconnectedCallback() {
    this.stopViewportTracking();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

